import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { LOCALES, STRINGS } from "./translations";

const LanguageContext = createContext(null);

const STORAGE_KEY = "rs-locale";

function detectLocale() {
  if (typeof window === "undefined") return "en";
  const stored = window.localStorage.getItem(STORAGE_KEY);
  if (stored && STRINGS[stored]) return stored;
  const nav = (window.navigator.language || "en").toLowerCase();
  const head = nav.split("-")[0];
  if (head === "zh") return "zh";
  if (head === "ja") return "ja";
  if (STRINGS[head]) return head;
  return "en";
}

export function LanguageProvider({ children }) {
  const [locale, setLocale] = useState("en");

  useEffect(() => {
    setLocale(detectLocale());
  }, []);

  useEffect(() => {
    if (typeof document !== "undefined") {
      document.documentElement.setAttribute("lang", locale);
    }
  }, [locale]);

  const value = useMemo(() => {
    const dict = STRINGS[locale] || STRINGS.en;
    const t = (key, vars) => {
      let str = dict[key] || STRINGS.en[key] || key;
      if (vars) {
        for (const k of Object.keys(vars)) {
          str = str.replace(new RegExp(`{${k}}`, "g"), String(vars[k]));
        }
      }
      return str;
    };
    const setLang = (code) => {
      if (!STRINGS[code]) return;
      setLocale(code);
      try {
        window.localStorage.setItem(STORAGE_KEY, code);
      } catch (_err) {
        // localStorage may be blocked (private mode / disabled storage) — ignore.
        void _err;
      }
    };
    return { locale, t, setLocale: setLang, locales: LOCALES };
  }, [locale]);

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export function useT() {
  const ctx = useContext(LanguageContext);
  if (!ctx) {
    // Safe fallback if used outside provider — never throws.
    return {
      locale: "en",
      t: (key) => STRINGS.en[key] || key,
      setLocale: () => {},
      locales: LOCALES,
    };
  }
  return ctx;
}
